<template>
  <div class="platform-container">
    <!-- Topbar Navigation -->
    <header class="topbar">
      <div class="topbar-left">
        <div class="brand">
          <BrandLogo :size="54" variant="header" />
        </div>
        <OrganizationTreeSelect
          v-if="showStoreSwitcher && !orgStoreError"
          :disabled="storeSwitcherDisabled"
        />
        <span v-else-if="orgStoreError" class="org-store-error">
          <AlertCircle :size="14" />
          {{ orgStoreError }}
        </span>
      </div>

      <div class="topbar-right">
        <div class="user-chip">
          <span class="user-avatar">{{ user.avatar }}</span>
          <span class="user-name">{{ user.name }}</span>
          <span class="user-role-tag">{{ user.roleLabel }}</span>
        </div>
        <button class="btn-ghost" @click="handleLogoutClick">退出</button>
      </div>
    </header>

    <!-- Workspace Main Layout -->
    <div class="layout">
      <!-- Expandable / Collapsible Sidebar (支持收缩与展开双模式) -->
      <aside class="sidebar-rail" :class="{ 'is-collapsed': isSidebarCollapsed }">
        <div class="rail-top">
          <nav class="rail-nav-section">
            <button
              v-for="item in navItems"
              :key="item"
              class="rail-item"
              :class="{ active: currentView === item }"
              @click="handleNav(item)"
              :title="NAV_LABELS[item]"
            >
              <div class="rail-icon-wrapper">
                <component :is="navIcons[item]" :size="isSidebarCollapsed ? 22 : 18" class="rail-icon" />
              </div>
              <span class="rail-label">{{ NAV_LABELS[item] }}</span>
            </button>
          </nav>
        </div>

        <div class="rail-bottom">
          <button
            type="button"
            class="rail-toggle-btn"
            :title="isSidebarCollapsed ? '展开菜单' : '收起菜单'"
            @click="toggleSidebar"
          >
            <component :is="isSidebarCollapsed ? ChevronRight : ChevronLeft" :size="16" class="toggle-icon" />
            <span v-if="!isSidebarCollapsed" class="toggle-label">收起侧栏</span>
          </button>

          <div class="rail-store-badge" :title="currentStore?.name || '鲸夕总店'">
            <BrandLogo :size="isSidebarCollapsed ? 24 : 20" />
            <span v-if="!isSidebarCollapsed" class="store-name-text">{{ currentStore?.name || '鲸夕总店' }}</span>
          </div>
        </div>
      </aside>

      <!-- Panel Area -->
      <main class="main-content">
        <div class="main-content-shell">
          <TemplatesPanel
            v-if="currentView === 'templates' || currentView === 'templates1'"
            title="模版库"
            query-source
          />
          <TasksPanel v-else-if="currentView === 'tasks' && !isNavViewHidden('tasks')" />
          <MaterialsPanel
            v-else-if="currentView === 'materials' || currentView === 'materials1'"
            title="素材库"
            query-source
          />
          <FontsPanel v-else-if="currentView === 'fonts'" />
          <StudioPanel v-else-if="currentView === 'studio'" />
          <ColorPalettePanel v-else-if="currentView === 'colorPalette'" />
          <SettingsPanel v-else-if="currentView === 'settings'" />
          <MembersPanel v-else-if="currentView === 'members'" />
          <PlatformPanelLoading v-if="isCurrentViewLoading" />
        </div>
      </main>
    </div>

    <Transition name="fade">
      <div v-if="toast.show" class="toast" :class="toast.type">{{ toast.message }}</div>
    </Transition>
    <TaskNotificationHost v-if="roleKey === 'designer' && !isNavViewHidden('tasks')" />
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Store, LayoutGrid, CheckSquare, FolderOpen, PenTool, Palette, Settings, Users, Type, AlertCircle, ChevronLeft, ChevronRight } from '@lucide/vue';
import { usePlatformStore } from '@/composables/usePlatformStore';
import { NAV_LABELS } from '@/data/platformData';
import { isNavViewHidden } from '@/data/authConfig';
import BrandLogo from '@/components/BrandLogo.vue';
import OrganizationTreeSelect from '@/components/platform/OrganizationTreeSelect.vue';
import TemplatesPanel from '@/components/platform/TemplatesPanel.vue';
import TasksPanel from '@/components/platform/TasksPanel.vue';
import MaterialsPanel from '@/components/platform/MaterialsPanel.vue';
import FontsPanel from '@/components/platform/FontsPanel.vue';
import StudioPanel from '@/components/platform/StudioPanel.vue';
import ColorPalettePanel from '@/components/platform/ColorPalettePanel.vue';
import SettingsPanel from '@/components/platform/SettingsPanel.vue';
import MembersPanel from '@/components/platform/MembersPanel.vue';
import TaskNotificationHost from '@/components/platform/TaskNotificationHost.vue';
import PlatformPanelLoading from '@/components/platform/PlatformPanelLoading.vue';
import { connectDesignerTaskRealtime, disconnectDesignerTaskRealtime } from '@/services/designerTaskRealtime';

const emit = defineEmits(['logout']);

const route = useRoute();
const router = useRouter();

const isSidebarCollapsed = ref(localStorage.getItem('jx_sidebar_collapsed') === 'true');

function toggleSidebar() {
  isSidebarCollapsed.value = !isSidebarCollapsed.value;
  try {
    localStorage.setItem('jx_sidebar_collapsed', String(isSidebarCollapsed.value));
  } catch {}
}

const {
  session, currentView, user, roleKey, navItems, currentStore, accessibleStores, toast, navigate, switchStore,
  loadOrganizationStores, isCurrentViewLoading, refreshModuleApi, orgStoreError, logout,
} = usePlatformStore();

function handleLogoutClick() {
  logout();
  emit('logout');
  router.push('/login');
}

watch(() => route.params.tab, (newTab) => {
  if (newTab && navItems.value.includes(newTab)) {
    navigate(newTab);
    return;
  }
  const fallback = navItems.value[0] || 'templates';
  if (newTab && newTab !== fallback) {
    router.replace(`/platform/${fallback}`);
  }
}, { immediate: true });

function handleNav(item) {
  if (route.params.tab !== item) {
    router.push(`/platform/${item}`);
  } else {
    refreshModuleApi(item);
  }
}

const navIcons = {
  templates: LayoutGrid,
  templates1: LayoutGrid,
  tasks: CheckSquare,
  materials: FolderOpen,
  materials: FolderOpen,
  fonts: Type,
  studio: PenTool,
  colorPalette: Palette,
  settings: Settings,
  members: Users,
};

const storeContextViews = new Set(['templates', 'templates1', 'materials', 'materials', 'studio']);
const isStoreContextView = computed(() => storeContextViews.has(currentView.value));
const showStoreSwitcher = computed(() =>
  isStoreContextView.value
  || (
    currentView.value !== 'fonts'
    && !(currentView.value === 'colorPalette' && roleKey.value === 'platform_admin')
    && (roleKey.value === 'platform_admin' || (user.value.stores?.length > 1))
  )
);
const storeSwitcherDisabled = computed(() =>
  isStoreContextView.value
  && roleKey.value !== 'platform_admin'
  && accessibleStores.value.length <= 1
);

function onStoreChange(e) {
  switchStore(e.target.value);
}

onMounted(() => {
  if (roleKey.value === 'designer' && !isNavViewHidden('tasks')) connectDesignerTaskRealtime();
  void loadOrganizationStores();
});

onUnmounted(() => disconnectDesignerTaskRealtime());
</script>

<style scoped>
.platform-container {
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f8fafc;
  font-family: var(--font-sans);
}

/* ==================== 顶栏 Topbar Navigation ==================== */
.topbar {
  height: 60px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  flex-shrink: 0;
  background: #ffffff;
  z-index: 50;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
}

.topbar-left, .topbar-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
}

.brand-titles {
  display: flex;
  flex-direction: column;
}

.brand-name {
  font-weight: 850;
  font-size: 17px;
  color: #0f172a;
  letter-spacing: -0.02em;
  line-height: 1.15;
}

.brand-slogan {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: #94a3b8;
  margin-top: 1px;
}

.org-store-error {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  color: #dc2626;
  background: #fef2f2;
  border: 1px solid #fecaca;
  padding: 5px 12px;
  border-radius: 999px;
  white-space: nowrap;
  line-height: 1.3;
}



/* 用户头像与角色 */

.user-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
}

.user-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  box-shadow: 0 2px 6px rgba(124, 58, 237, 0.25);
}

.user-role-tag {
  font-size: 11px;
  font-weight: 650;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f5f3ff;
  color: #7c3aed;
  border: 1px solid #ddd6fe;
  line-height: 1.3;
}

.btn-ghost {
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s ease;
}

.btn-ghost:hover {
  background: #f1f5f9;
  color: #0f172a;
  border-color: #cbd5e1;
}

/* ==================== 双栏主体布局 ==================== */
.layout {
  flex: 1;
  display: flex;
  overflow: hidden;
  background: #f8fafc;
}

/* 68px 垂直活动栏 (1:1 参考图浅紫圆角选中卡片) */
/* ==================== 侧边栏：支持展开与收缩双模式 ==================== */
.sidebar-rail {
  width: 200px;
  border-right: 1px solid #f1f5f9;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 16px 12px;
  flex-shrink: 0;
  background: #ffffff;
  z-index: 20;
  transition: width 0.22s cubic-bezier(0.16, 1, 0.3, 1), padding 0.22s cubic-bezier(0.16, 1, 0.3, 1);
}

.sidebar-rail.is-collapsed {
  width: 72px;
  padding: 16px 8px;
  align-items: center;
}

.rail-top {
  width: 100%;
}

.rail-nav-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.sidebar-rail.is-collapsed .rail-nav-section {
  gap: 12px;
  align-items: center;
}

/* 导航项 - 展开模式：左图右文横向排布 */
.rail-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 42px;
  padding: 8px 14px;
  border-radius: 10px;
  border: none;
  background: transparent;
  color: #475569;
  cursor: pointer;
  box-sizing: border-box;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

/* 导航项 - 收缩模式：上图下文垂直居中 */
.sidebar-rail.is-collapsed .rail-item {
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 58px;
  min-height: 58px;
  padding: 6px 2px;
  border-radius: 14px;
}

.rail-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 20px;
  line-height: 1;
  color: inherit;
  flex-shrink: 0;
}

.sidebar-rail.is-collapsed .rail-icon-wrapper {
  min-height: 22px;
}

.rail-icon {
  color: inherit;
  transition: transform 0.2s ease;
}

.rail-label {
  font-size: 13.5px;
  line-height: 1.3;
  font-weight: 550;
  color: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-rail.is-collapsed .rail-label {
  font-size: 11px;
  line-height: 1.2;
  font-weight: 550;
  letter-spacing: -0.01em;
}

.rail-item:hover {
  background: #f8fafc;
  color: #0f172a;
  transform: translateY(-1px);
}

.rail-item.active {
  background: #f3e8ff;
  color: #9333ea;
  font-weight: 750;
}

.sidebar-rail.is-collapsed .rail-item.active {
  box-shadow: 0 2px 8px rgba(147, 51, 234, 0.08);
}

.rail-item.active .rail-label {
  font-weight: 750;
  color: #9333ea;
}

.rail-item.active:hover {
  background: #ede9fe;
  color: #7c3aed;
}

/* 底部区域与展开/收起切换按钮 */
.rail-bottom {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
}

.sidebar-rail.is-collapsed .rail-bottom {
  align-items: center;
}

.rail-toggle-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 34px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid transparent;
  background: #f8fafc;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}

.sidebar-rail.is-collapsed .rail-toggle-btn {
  width: 38px;
  height: 38px;
  padding: 0;
  justify-content: center;
  border-radius: 10px;
}

.rail-toggle-btn:hover {
  background: #f1f5f9;
  color: #0f172a;
  border-color: #e2e8f0;
}

.toggle-icon {
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.toggle-label {
  white-space: nowrap;
}

.rail-store-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 8px;
  background: #f8fafc;
  cursor: pointer;
  transition: background 0.15s ease;
}

.sidebar-rail.is-collapsed .rail-store-badge {
  padding: 6px;
  justify-content: center;
  background: transparent;
}

.rail-store-badge:hover {
  background: #f1f5f9;
}

.store-name-text {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.main-content {
  position: relative;
  flex: 1;
  overflow-y: auto;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
}

.main-content-shell {
  position: relative;
  flex: 1;
  min-height: 100%;
}



.toast {
  position: fixed;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(15, 23, 42, 0.9);
  backdrop-filter: blur(12px);
  color: #ffffff;
  padding: 10px 22px;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 500;
  z-index: 13000;
  box-shadow: 0 16px 36px -4px rgba(15, 23, 42, 0.25);
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  letter-spacing: 0.01em;
}

.toast.success {
  background: rgba(22, 101, 52, 0.95);
  border-color: rgba(74, 222, 128, 0.35);
}

.toast.warn {
  background: rgba(180, 83, 9, 0.95);
  border-color: rgba(251, 191, 36, 0.35);
}

.toast.error {
  background: rgba(185, 28, 28, 0.95);
  border-color: rgba(248, 113, 113, 0.35);
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.fade-enter-from {
  opacity: 0;
  transform: translate(-50%, 10px) scale(0.96);
}

.fade-leave-to {
  opacity: 0;
  transform: translate(-50%, -8px) scale(0.96);
}
</style>
